import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  Audience,
  ConcreteExperienceMessage,
  getDenormalizedSubTasks,
  getSubTaskTemplates,
  IExperienceTemplate,
  ITaskDenormalized,
  RequirementType,
  SubTaskStatus,
  SubTaskType,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { ScheduleChannel } from '@dadohr/shared/domainTypes/schedules';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  TextField,
  Typography
} from '@material-ui/core';
import { sortBy } from 'lodash';
import { ChevronDown, Redo, RotateCw, Undo } from 'lucide-react';
import moment from 'moment-timezone';
import { useMemo, useState } from 'react';
import { ButtonWithDropdownOption } from '../../../../../components/ButtonWithDropdownOption';
import { ButtonWithoutPropagation } from '../../../../../components/ButtonWithoutPropagation';
import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import {
  ColoredChipDark,
  ColoredChipLight
} from '../../../../../components/ColoredChip';
import { Loader } from '../../../../../components/Loader';
import { OptionalHint } from '../../../../../components/OptionalHint';
import { SubTaskAsText } from '../../../../../components/SubTask';
import { SubTaskIcon } from '../../../../../components/SubTaskIcon';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { usePromise } from '../../../../../hooks/usePromise';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../layout/Flex';
import { joinWithCommaAndAnd } from '../../../../../services/enumerate';
import { getItemDisplayName, getItemDisplayPair } from '../../../helpers/adminName';
import { resendExperienceItem } from '../../../../../services/experiences/instance';
import { getTriggeredItemsForTask } from '../../../../../services/experiences/template';
import { useCurrentUser } from '../../../../../services/currentUser';
import {
  ExperienceReplacements,
  interpolateExperienceText
} from '../../../../../services/set/interpolation';
import { LightMdToJsx } from '../../../../../services/formatter';
import {
  fromMoment,
  Timestamp,
  toMoment,
  tsIsAfter
} from '../../../../../services/time';
import { getDocusignEnvelopeStatus } from '../../../../Org/pages/Integrations/integrations/docusign/service';
import { ActorDescriptor } from '../../../components/Actors';
import { InDeliveryChip } from '../../../components/StatusChip';
import { TaskStatusChip } from '../../../components/TaskStatusChip';
import { ActorLegend } from './ActorLegend';
import { AudienceHint } from './AudienceHint';
import {
  InnerItemGroup,
  InnerItemList,
  InnerItemRow,
  ItemTitle
} from './CommonStyledComponents';
import { CustomizedTimingHint } from './CustomizedTimingHint';
import { DateWithTooltip } from './DateWithTooltip';
import { ForceTriggerButton } from './ForceTriggerButton';
import { SECOND_MESSAGE_STATUS_CUT_OFF } from './MessageDoneSendStatus';
import { ModifiedChip } from './ModifiedChip';
import { SelectableListItem } from './SelectableListItem';
import { SkipLastSubTaskButton } from './SkipLastSubtaskButton';
import { TaskCompletionCriteria } from './TaskCompletionCriteria';

export const SubTaskStatusChip = ({ status }: { status: SubTaskStatus }) => {
  const { t } = useUnsafeTranslation();
  switch (status) {
    case SubTaskStatus.DONE:
      return (
        <ColoredChipLight color="green" size="small">
          {t('Completed')}
        </ColoredChipLight>
      );
    case SubTaskStatus.SKIPPED:
      return (
        <ColoredChipLight color="darkGray" size="small">
          {t('Skipped')}
        </ColoredChipLight>
      );
    case SubTaskStatus.OPEN:
      return (
        <ColoredChipLight color="gray" size="small">
          {t('Pending')}
        </ColoredChipLight>
      );
    default:
      return null;
  }
};

//This prevents the icon from shrinking when the text to the right is higher than one line
const IconContainer = styled('div')((p) => ({
  minWidth: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

/*
const PreSkipButton = ({}: {
  subTaskTplId: string;
  triggers: ITaskDenormalized[];
  preSkips: IExperience['preSkips'];
  onPreSkipSubTask: (subTaskTplId: string) => void;
  onPreUnskipSubTask: (subTaskTplId: string) => void;
}) => {
  return null;
};
*/

const getResourceForDocusignSubTask = (
  tpl: Doc<IExperienceTemplate>,
  taskTplId: string,
  subTaskTplId: string
) => {
  const taskTpl = tpl.data.tasks.find((t) => t.id === taskTplId);
  if (taskTpl) {
    const subTaskTpl = getSubTaskTemplates(taskTpl).find(
      (s) => s.id === subTaskTplId
    );
    if (subTaskTpl?.type === SubTaskType.DOCUSIGN_ENVELOPE_SIGN) {
      return subTaskTpl.data.resourceId;
    }
  }
  return null;
};

const findAllAffectedItemsForDocusignRestartRequest = (
  tpl: Doc<IExperienceTemplate>,
  taskTplId: string,
  subTaskTplId: string
) => {
  const taskTpl = tpl.data.tasks.find((t) => t.id === taskTplId);
  if (!taskTpl) {
    return [];
  }
  const subTaskTpl = getSubTaskTemplates(taskTpl).find(
    (s) => s.id === subTaskTplId
  );
  if (subTaskTpl?.type !== SubTaskType.DOCUSIGN_ENVELOPE_SIGN) {
    return [];
  }

  const affectedResourceId = subTaskTpl.data.resourceId;
  const res: {
    taskTplId: string;
    subTaskTplId: string;
    resourceId: string;
  }[] = [];
  tpl.data.tasks.forEach((task) => {
    getSubTaskTemplates(task).forEach((subTask) => {
      if (
        subTask.type === SubTaskType.DOCUSIGN_ENVELOPE_SIGN &&
        subTask.data.resourceId === affectedResourceId
      ) {
        res.push({
          taskTplId: task.id,
          subTaskTplId: subTask.id,
          resourceId: affectedResourceId
        });
      }
    });
  });
  return res;
};

const RestartSignatureProcessDialog = ({
  open,
  onClose,
  restartText,

  tpl,
  instanceId,
  taskTplId,
  subTaskTplId,
  resourceId,
  onRestart
}: {
  open: boolean;
  onClose: () => void;
  restartText: string;

  tpl: Doc<IExperienceTemplate>;
  instanceId: string;
  taskTplId: string;
  subTaskTplId: string;
  resourceId: string;
  onRestart: (
    trigger: {
      taskTplId: string;
      subTaskTplId: string;
      resourceId: string;
    },
    items: {
      taskTplId: string;
      subTaskTplId: string;
      resourceId: string;
    }[],
    voidedReason: string | null
  ) => Promise<void>;
}) => {
  const { t } = useUnsafeTranslation();
  const [voidedReason, setVoidedReason] = useState('');

  const [envStatus, envStatusLoading] = usePromise(async () => {
    return getDocusignEnvelopeStatus(
      tpl.data.orgId,
      instanceId,
      resourceId
    ).then((x) => x.status);
  }, [instanceId, tpl.data.orgId, resourceId]);
  const isComplete = envStatus === 'completed' || envStatus === 'signed';
  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{restartText}?</DialogTitle>
      <DialogContent>
        {envStatusLoading ? (
          <Loader height={190} />
        ) : (
          <div /* This div is required - otherwise the MUI dialog adds strange scrollbars */
          >
            <FlexContainerVertical spacing={2}>
              {isComplete ? (
                <LightMdToJsx>
                  {t(
                    'Restarting the signature process will generate a new signature request and send it to **all signers and other recipients**. A new Docusign notification will be sent, but no additional Dado notifications.'
                  )}
                </LightMdToJsx>
              ) : (
                <>
                  <LightMdToJsx>
                    {t(
                      'Restarting the signature process will void the existing signature request, then generate a new signature request and send it to **all signers and other recipients**. A new Docusign notification will be sent, but no additional Dado notifications.'
                    )}
                  </LightMdToJsx>

                  <TextField
                    label={t(
                      'Reason for restarting (visible to signers and recipients)'
                    )}
                    fullWidth
                    value={voidedReason}
                    onChange={(ev) => setVoidedReason(ev.target.value)}
                  />
                </>
              )}

              <Typography variant="caption">
                {t('Be aware')}:{' '}
                {t(
                  `restarting will save all other changes you've made to this experience`
                )}
                .
              </Typography>
            </FlexContainerVertical>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <ButtonWithoutPropagation onClick={() => onClose()}>
          {t('Cancel')}
        </ButtonWithoutPropagation>
        <ButtonWithPromise
          color="primary"
          variant="contained"
          onClick={async (ev) => {
            ev.stopPropagation();
            ev.preventDefault();
            await onRestart(
              { taskTplId, subTaskTplId, resourceId },
              findAllAffectedItemsForDocusignRestartRequest(
                tpl,
                taskTplId,
                subTaskTplId
              ),
              isComplete ? null : voidedReason
            );
            onClose();
          }}
          disabled={(!isComplete && !voidedReason) || envStatusLoading}
          autoFocus
          pending={t('Resending...')}
        >
          {restartText}
        </ButtonWithPromise>
      </DialogActions>
    </Dialog>
  );
};

export const RestartSignatureProcess = ({
  tpl,
  instanceId,
  taskTplId,
  subTaskTplId,
  onRestart
}: {
  tpl: Doc<IExperienceTemplate>;
  instanceId: string;
  taskTplId: string;
  subTaskTplId: string;
  onRestart: (
    trigger: {
      taskTplId: string;
      subTaskTplId: string;
      resourceId: string;
    },
    items: {
      taskTplId: string;
      subTaskTplId: string;
      resourceId: string;
    }[],
    voidedReason: string | null
  ) => Promise<void>;
}) => {
  const { t } = useUnsafeTranslation();
  const dialog = useDialogState();
  const restartText = t('Restart signature process');
  const resourceId = getResourceForDocusignSubTask(
    tpl,
    taskTplId,
    subTaskTplId
  );
  if (!resourceId) {
    return null;
  }
  return (
    <>
      <ButtonWithoutPropagation
        startIcon={<RotateCw size={16} />}
        variant="outlined"
        type="button"
        size="small"
        onClick={() => dialog.openDialog()}
      >
        {restartText}
      </ButtonWithoutPropagation>
      {dialog.dialogOpen && (
        <RestartSignatureProcessDialog
          open={true}
          onClose={() => dialog.closeDialog()}
          tpl={tpl}
          instanceId={instanceId}
          restartText={restartText}
          taskTplId={taskTplId}
          subTaskTplId={subTaskTplId}
          resourceId={resourceId}
          onRestart={onRestart}
        />
      )}
    </>
  );
};

const ResendVsRestartSignatureConfirmationDialog = ({
  open,
  onClose,
  onResend
}: {
  open: boolean;
  onClose: () => void;
  onResend: () => Promise<void>;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t('Resend?')}</DialogTitle>
      <DialogContent>
        <LightMdToJsx>
          {t(
            'This will just resend the the Dado notification, and will not resend the signature request via Docusign. If you wish to resend the Docusign request, cancel and click the **Restart signature process** button instead.'
          )}
        </LightMdToJsx>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <ButtonWithPromise
          pending={t('Resending...')}
          variant="contained"
          color="primary"
          onClick={() => onResend().then(onClose)}
        >
          {t('Resend')}
        </ButtonWithPromise>
      </DialogActions>
    </Dialog>
  );
};

const TaskStartDateWithConcreteMessagesCheck = ({
  task,
  startMessages
}: {
  task: ITaskDenormalized;
  startMessages: ConcreteExperienceMessage[];
}) => {
  const { t } = useUnsafeTranslation();
  const realStartDate = useMemo(() => {
    const startMessage = startMessages[0];
    if (!startMessage) {
      return null;
    }
    return {
      date: startMessage.at,
      isSameDay: toMoment(task.start).isSame(toMoment(startMessage.at), 'day')
    };
  }, [task, startMessages]);

  if (!realStartDate) {
    return (
      <DateWithTooltip textBefore={`${t('Started on')}`} date={task.start} />
    );
  }

  if (realStartDate.isSameDay) {
    return (
      <DateWithTooltip
        textBefore={`${t('Started on')}`}
        date={realStartDate.date}
      />
    );
  } else {
    return (
      <>
        <DateWithTooltip
          textBefore={`${t('Should have started on')}`}
          date={task.start}
        />
        {' • '}
        <DateWithTooltip
          textBefore={`${t('Started on')}`}
          date={realStartDate.date}
        />
      </>
    );
  }
};

const TaskStartDate = ({
  task,
  startMessages
}: {
  task: ITaskDenormalized;
  startMessages: ConcreteExperienceMessage[];
}) => {
  const { t } = useUnsafeTranslation();
  if (task.status === TaskStatus.SKIPPED) {
    return (
      <DateWithTooltip
        textBefore={`${t('Would have started on')}`}
        date={task.start}
      />
    );
  }
  if (task.status === TaskStatus.NOT_STARTED) {
    return (
      <DateWithTooltip textBefore={`${t('Starts on')}`} date={task.start} />
    );
  }
  return (
    <TaskStartDateWithConcreteMessagesCheck
      task={task}
      startMessages={startMessages}
    />
  );
};

const TaskStatusWithConcreteMessagesCheck = ({
  task,
  startMessages
}: {
  task: ITaskDenormalized;
  // responsibility of the caller to filter these out correctly
  startMessages: ConcreteExperienceMessage[];
}) => {
  const { t } = useUnsafeTranslation();
  if (task.start.toMillis() < SECOND_MESSAGE_STATUS_CUT_OFF) {
    return <TaskStatusChip status={task.status} autoWidth />;
  }
  if (
    task.status === TaskStatus.COMPLETE ||
    task.status === TaskStatus.SKIPPED ||
    task.status === TaskStatus.NOT_STARTED
  ) {
    return <TaskStatusChip status={task.status} autoWidth />;
  }
  if (!startMessages.length) {
    return <InDeliveryChip />;
  }
  if (!!startMessages.find((m) => m.status === 'OK')) {
    return <TaskStatusChip status={task.status} autoWidth />;
  }
  // Closes new-main-1cwf: a BOUNCED/ERROR message must surface as Error even when
  // another message is still QUEUED. Without this short-circuit the QUEUED branch
  // below renders "In delivery", hiding a real delivery failure on the timeline.
  if (
    !!startMessages.find(
      (m) => m.status === 'BOUNCED' || m.status === 'ERROR'
    )
  ) {
    return (
      <ColoredChipDark size="small" color="red">
        {t('Error')}
      </ColoredChipDark>
    );
  }
  if (!!startMessages.find((m) => m.status === 'QUEUED')) {
    return <InDeliveryChip />;
  }

  return (
    <ColoredChipDark size="small" color="red">
      {t('Error')}
    </ColoredChipDark>
  );
};

export const Task = ({
  tpl,
  task,
  onSkipTask,
  onUnSkipTask,
  onSkipSubTask,
  onSkipLastSubTask,
  onOpenSubTask,
  onSendToday,
  onRestartDocusignSubTask,
  onForceTrigger,
  actor,
  channels,
  selected,
  onSelect,
  audience,
  orgId,
  customizedTiming,
  isModified,
  isSubtaskModified,
  isSelectable,
  triggeredBy,
  concreteMessagesForTask = EMPTY_ARR,
  template,
  isCCView,
  replacements
}: {
  tpl: Doc<IExperienceTemplate>;
  task: ITaskDenormalized;
  onSkipTask: (taskId: string) => void;
  onUnSkipTask: (taskId: string) => void;
  onSkipSubTask: (subTaskId: string) => void;
  onSkipLastSubTask: (subTaskId: string) => Promise<any>;
  onOpenSubTask: (subTaskId: string) => void;
  onSendToday: () => void;
  onRestartDocusignSubTask:
    | ((
        trigger: {
          taskTplId: string;
          subTaskTplId: string;
          resourceId: string;
        },
        items: {
          taskTplId: string;
          subTaskTplId: string;
          resourceId: string;
        }[],
        voidedReason: string | null
      ) => Promise<void>)
    | null;
  onForceTrigger?: () => Promise<any>; 
  actor: ActorDescriptor;
  channels: ScheduleChannel[];
  selected?: boolean;
  orgId: string;
  onSelect?: (nextSelected: boolean) => void;
  audience?: Audience | null;
  customizedTiming?: boolean;
  isModified?: boolean;
  isSubtaskModified?: (taskId: string, subtaskId: string) => boolean;
  isSelectable?: boolean;
  triggeredBy?: ITaskDenormalized[];
  concreteMessagesForTask?: ConcreteExperienceMessage[];
  template?: IExperienceTemplate;
  isCCView?: boolean;
  replacements?: ExperienceReplacements;
}) => {
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const subTasks = useMemo(() => getDenormalizedSubTasks(task), [task]);

  const [resendConfirmationRequest, setResendConfirmationRequest] = useState<{
    type: 'DOCUSIGN_SIGNATURE_REQUEST_INFO';
    onResend: () => Promise<void>;
  } | null>(null);

  const handleChannelSelectToResend = async ({
    orgId,
    experienceId,
    itemId,
    actor,
    channels
  }: {
    orgId: string;
    experienceId: string;
    itemId: string;
    actor: string;
    channels: ScheduleChannel[];
  }) => {
    const performResend = async () => {
      await resendExperienceItem({
        orgId,
        experienceId,
        itemIds: [itemId],
        itemType: 'TASK',
        actor,
        channels
      })
        .then(() => enqueueSnackbarSuccess('Task resent successfully!'))
        .catch(() =>
          enqueueSnackbarError('Failed to resent task! Please try again later!')
        );
    };
    if (
      !!task.blocks.find((b) => b.type === SubTaskType.DOCUSIGN_ENVELOPE_SIGN)
    ) {
      setResendConfirmationRequest({
        type: 'DOCUSIGN_SIGNATURE_REQUEST_INFO',
        onResend: performResend
      });
    } else {
      await performResend();
    }
  };

  const isTaskAfterToday = (start: Timestamp): boolean => {
    const tomorrow = moment().startOf('day').add(1, 'days');
    const isAfterToday: boolean = tsIsAfter(start, fromMoment(tomorrow));
    return isAfterToday;
  };

  const isUntriggered = !!triggeredBy?.length;

  const taskStartMessages = useMemo(() => {
    return sortBy(
      concreteMessagesForTask.filter(
        (m) =>
          m.type === 'TASK_START' ||
          m.type === 'TASK_START_PER_COHORT' ||
          m.type === 'EXPERIENCE_MESSAGE' // can encompass task starts
      ),
      (m) => m.at.toMillis()
    );
  }, [concreteMessagesForTask]);

  // if parents are skipped,
  // then pre-skips are not possible.
  // It's also not possible to unskip in this case

  const taskTpl = tpl.data.tasks.find((tpl) => tpl.id === task.templateId);
  const { primary: rawPrimary, secondary: rawSecondary } = getItemDisplayPair(taskTpl ?? task);
  // Prefer the per-instance `replacements` built by `_mapFn` (knows the
  // participant + supporting-actor users) so tokens like
  // {{ participant.fullName }} resolve. Fall back to a roles-less object
  // for call sites that don't yet thread replacements through (e.g. the
  // manual-enroll preview, where actor users aren't loaded).
  const interpolationReplacements: ExperienceReplacements = replacements ?? {
    experience: { vars: { title: tpl.data.title, tasks: {} } },
    org: { vars: { name: org.name } },
    roles: []
  };
  const interpolationOpts = { dateFormat: 'YYYY/MM/DD' };
  const displayPrimary = interpolateExperienceText(rawPrimary, interpolationReplacements, interpolationOpts);
  const displaySecondary = rawSecondary
    ? interpolateExperienceText(rawSecondary, interpolationReplacements, interpolationOpts)
    : undefined;

  return (
    <>
      <SelectableListItem
        isSelectable={
          /* looks weird but is needed because of default */
          isUntriggered ? false : isSelectable
        }
        selected={selected}
        onSelect={onSelect}
        containerStyle={isCCView ? { opacity: 0.5 } : undefined}
        header={
          <>
            <TaskStatusWithConcreteMessagesCheck
              task={task}
              startMessages={taskStartMessages}
            />
            <ActorLegend type="task" color={actor.color} isCCView={isCCView}>
              {actor.label}
            </ActorLegend>
            <ItemTitle>
              <strong>{displayPrimary}</strong>
              {displaySecondary && (
                <span style={{ fontStyle: 'italic' }}>
                  {' '}"{displaySecondary}"
                </span>
              )}
              <AudienceHint type="task" audience={audience} />
              <CustomizedTimingHint
                type="task"
                customizedTiming={customizedTiming}
                at={task.start}
              />
              <ModifiedChip active={isModified} />
            </ItemTitle>
          </>
        }
        headerActions={
          <>
            {!isUntriggered && (
              <>
                {task.status !== TaskStatus.NOT_STARTED &&
                  task.status !== TaskStatus.SKIPPED && (
                    <ButtonWithDropdownOption
                      size="small"
                      variant="outlined"
                      pending={t('Sending...')}
                      options={channels}
                      allOptionLabel={t('All channels')}
                      endIcon={<ChevronDown size={16} />}
                      onSelect={async (selectedChannel: ScheduleChannel[]) =>
                        handleChannelSelectToResend({
                          orgId,
                          experienceId: task.experienceId,
                          itemId: task.id,
                          actor: actor.key,
                          channels: selectedChannel
                        })
                      }
                    >
                      {t('Resend')}
                    </ButtonWithDropdownOption>
                  )}
                {task.status !== TaskStatus.COMPLETE &&
                  task.status !== TaskStatus.SKIPPED && (
                    <ButtonWithoutPropagation
                      startIcon={<Redo size={16} />}
                      variant="outlined"
                      type="button"
                      size="small"
                      onClick={() => onSkipTask(task.id)}
                    >
                      {t('Skip')}
                    </ButtonWithoutPropagation>
                  )}
                {task.status === TaskStatus.SKIPPED && (
                    <ButtonWithoutPropagation
                      startIcon={<Undo size={16} />}
                      variant="outlined"
                      type="button"
                      size="small"
                      onClick={() => onUnSkipTask(task.id)}
                    >
                      {t('Unskip')}
                    </ButtonWithoutPropagation>
                  )}
                {task.status === TaskStatus.NOT_STARTED &&
                  isTaskAfterToday(task.start) && (
                    <ButtonWithoutPropagation
                      variant="outlined"
                      type="button"
                      size="small"
                      onClick={onSendToday}
                    >
                      {t('Start today')}
                    </ButtonWithoutPropagation>
                  )}
              </>
            )}
            
            {template &&
              <ForceTriggerButton
                item={task}
                itemType={'task'}
                triggeredBy={triggeredBy || []}
                isUntriggered={isUntriggered}
                template={template}
                onForceTrigger={onForceTrigger}
                enqueueSnackbarSuccess={enqueueSnackbarSuccess}
                enqueueSnackbarError={enqueueSnackbarError}
                t={t}
              />
            }
          </>
        }
        subHeader={
          <>
            <TaskCompletionCriteria
              criteria={task.customCompletionCriteria}
              totalSubTaskCount={subTasks.length}
            />
            {task.customCompletionCriteria.length !== 0 && (
              <Typography variant="caption" component="div">
                •
              </Typography>
            )}

            {triggeredBy?.length ? (
              <Typography variant="caption" component="div">
                {t('After completion of')}:{' '}
                <strong>
                  {joinWithCommaAndAnd(
                    triggeredBy.map((x) => getItemDisplayName(x)),
                    t
                  )}
                </strong>
              </Typography>
            ) : (
              <Typography variant="caption" component="div">
                <TaskStartDate task={task} startMessages={taskStartMessages} />{' '}
                • <DateWithTooltip textBefore={`${t('Due')}`} date={task.end} />{' '}
                {task.status === TaskStatus.COMPLETE && (
                  <>
                    •{' '}
                    <DateWithTooltip
                      textBefore={`${t('Completed on')}`}
                      date={task.completedAt || task.end}
                    />
                  </>
                )}
              </Typography>
            )}
          </>
        }
      >
        <InnerItemList>
          <InnerItemGroup>
            {subTasks.map((s) => (
              <InnerItemRow key={s.id}>
                <FlexContainer>
                  <SubTaskStatusChip status={s.status} />
                  <FlexContainer justifyContent="flex-start" spacing={0.5}>
                    <IconContainer>
                      <SubTaskIcon key={`${s.id}-icon`} s={s} />
                    </IconContainer>
                    <SubTaskAsText key={`${s.id}-text`} s={s} />
                    {s.requirementType === RequirementType.OPTIONAL && (
                      <OptionalHint />
                    )}
                  </FlexContainer>
                  <ModifiedChip active={isSubtaskModified?.(task.id, s.id)} />
                </FlexContainer>
                {!isUntriggered && (
                  <FlexContainer justifyContent="flex-end">
                    {s.status === SubTaskStatus.OPEN && (
                      <>
                        {(() => {
                          const openSubTasks = task.subTasks.filter(st => st.status === SubTaskStatus.OPEN && st.requirementType === RequirementType.MANDATORY);
                          const isLastSubTask = openSubTasks.length === 1 && openSubTasks[0].id === s.id;
                          const triggeredItems = getTriggeredItemsForTask(tpl, task.templateId);
                          const hasCompletedSubTasks = task.subTasks.some(st => st.status === SubTaskStatus.DONE);
                          
                          if (isLastSubTask && triggeredItems?.length && hasCompletedSubTasks) {
                            return (
                              <SkipLastSubTaskButton
                                subTask={s}
                                task={task}
                                template={tpl}
                                onSkipNoTrigger={onSkipSubTask}
                                onSkipSubTask={onSkipLastSubTask}
                                enqueueSnackbarSuccess={enqueueSnackbarSuccess}
                                enqueueSnackbarError={enqueueSnackbarError}
                                t={t}
                              />
                            );
                          } else {
                            return (
                              <ButtonWithoutPropagation
                                startIcon={<Redo size={16} />}
                                variant="outlined"
                                type="button"
                                size="small"
                                onClick={() => onSkipSubTask(s.id)}
                              >
                                {t('Skip')}
                              </ButtonWithoutPropagation>
                            );
                          }
                        })()}
                      </>
                    )}

                    {s.status === SubTaskStatus.SKIPPED && (
                        <ButtonWithoutPropagation
                          startIcon={<Undo size={16} />}
                          variant="outlined"
                          type="button"
                          size="small"
                          onClick={() => onOpenSubTask(s.id)}
                        >
                          {t('Unskip')}
                        </ButtonWithoutPropagation>
                      )}
                    {s.status === SubTaskStatus.DONE &&
                      s.type !== SubTaskType.DOCUSIGN_ENVELOPE_SIGN && (
                        <ButtonWithoutPropagation
                          startIcon={<Undo size={16} />}
                          variant="outlined"
                          type="button"
                          size="small"
                          onClick={() => onOpenSubTask(s.id)}
                        >
                          {t('Uncomplete')}
                        </ButtonWithoutPropagation>
                      )}
                    {onRestartDocusignSubTask &&
                      s.type === SubTaskType.DOCUSIGN_ENVELOPE_SIGN &&
                      task.status !== TaskStatus.NOT_STARTED && (
                        <RestartSignatureProcess
                          tpl={tpl}
                          instanceId={task.experienceId}
                          taskTplId={task.templateId}
                          subTaskTplId={s.templateId}
                          onRestart={onRestartDocusignSubTask}
                        />
                      )}
                  </FlexContainer>
                )}
              </InnerItemRow>
            ))}
          </InnerItemGroup>
        </InnerItemList>
      </SelectableListItem>
      {resendConfirmationRequest?.type ===
        'DOCUSIGN_SIGNATURE_REQUEST_INFO' && (
        <ResendVsRestartSignatureConfirmationDialog
          open={true}
          onClose={() => setResendConfirmationRequest(null)}
          onResend={resendConfirmationRequest.onResend}
        />
      )}
    </>
  );
};
